import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, EmptyState, Input, Spinner } from '@adop/ui';
import { MapPin, Search, Store } from 'lucide-react';
import { MARKETPLACE_PAGE_SIZE, normalizeBranding } from '@adop/shared';
import type { LocationCard, MarketplaceResponse } from '@adop/shared';
import { apiFetch, errorMessage } from '../lib/api';
import { mediaUrl } from '../lib/format';
import { useAuth } from '../lib/auth-context';
import { devAuthEnabledFromImportMeta } from '../lib/env';
import { Alert } from '../components/Alert';
import { Brand } from '../components/Brand';
import { DevAuthForm } from '../components/DevAuthForm';

export function MarketplacePage() {
  const { session, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MarketplaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (query.trim().length > 0) params.set('q', query.trim());
    const timer = window.setTimeout(() => {
      apiFetch<MarketplaceResponse>(`/api/platform/marketplace?${params.toString()}`)
        .then((response) => {
          if (active) {
            setData(response);
            setError(null);
          }
        })
        .catch((cause: unknown) => {
          if (active) setError(errorMessage(cause));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, page]);

  const handleRestaurantClick = async () => {
    setSignInError(null);
    if (session) {
      navigate('/dashboard');
      return;
    }
    try {
      await signInWithGoogle();
    } catch (cause) {
      setSignInError(errorMessage(cause));
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / MARKETPLACE_PAGE_SIZE)) : 1;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Brand />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-zinc-500 sm:inline">For restaurants:</span>
            <Button variant="ghost" onClick={handleRestaurantClick}>
              {session ? 'Dashboard' : 'Log in'}
            </Button>
            <Button onClick={() => navigate('/register')}>Register</Button>
          </div>
        </div>
      </header>

      {!session && devAuthEnabledFromImportMeta() ? (
        <div className="border-b border-amber-200 bg-amber-50/60">
          <div className="mx-auto flex max-w-md flex-col gap-2 px-4 py-4">
            <p className="text-xs text-amber-800">
              Local development: sign in with a seeded account (owner@adop.test, manager@adop.test
              or staff@adop.test — password <code className="font-mono">adopdev123</code>).
            </p>
            <DevAuthForm onSignedIn={() => navigate('/dashboard')} />
          </div>
        </div>
      ) : null}

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
            Order takeout from local restaurants
          </h1>
          <p className="mt-2 text-zinc-500">
            Discover restaurants near you, order to-go, and reserve a table in seconds.
          </p>
        </div>

        {signInError ? (
          <div className="mx-auto mt-4 max-w-md">
            <Alert>{signInError}</Alert>
          </div>
        ) : null}

        <div className="mx-auto mt-6 max-w-md">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search by name or city"
              className="pl-9"
              aria-label="Search restaurants"
            />
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <Alert>{error}</Alert>
          ) : data && data.locations.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.locations.map((card) => (
                <LocationCardView key={card.location_id} card={card} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Store className="h-6 w-6" />}
              title="No restaurants found"
              description={query ? 'Try a different search.' : 'Published restaurants will appear here.'}
            />
          )}
        </div>

        {data && data.total > MARKETPLACE_PAGE_SIZE ? (
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="text-sm text-zinc-500">
              Page {page} of {totalPages}
            </span>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        ) : null}
      </main>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-6 text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} adop</span>
          <div className="flex gap-4">
            <Link to="/pricing" className="hover:text-zinc-900">
              Pricing
            </Link>
            <Link to="/terms" className="hover:text-zinc-900">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-zinc-900">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function LocationCardView({ card }: { card: LocationCard }) {
  const branding = normalizeBranding(card.branding);
  const logo = mediaUrl(branding.logo_key);
  const cover = mediaUrl(branding.cover_key);

  return (
    <a
      href={card.storefront_url}
      className="group block overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="relative h-32 w-full bg-zinc-100">
        {cover ? (
          <img src={cover} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full" style={{ backgroundColor: branding.primary_color }} />
        )}
        <span className="absolute right-2 top-2">
          {card.open_now ? (
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              Open now
            </span>
          ) : (
            <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
              Closed
            </span>
          )}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-zinc-200 bg-white">
            {logo ? (
              <img src={logo} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="font-semibold text-zinc-500">{card.name.charAt(0)}</span>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-zinc-900">{card.name}</h3>
            {card.city ? (
              <p className="flex items-center gap-1 text-xs text-zinc-500">
                <MapPin className="h-3 w-3" /> {card.city}
              </p>
            ) : null}
          </div>
        </div>
        {card.about ? <p className="mt-2 line-clamp-2 text-sm text-zinc-600">{card.about}</p> : null}
        {card.tags.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-1">
            {card.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </a>
  );
}
